import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import TierBadge from "@/components/TierBadge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Award, Users, Briefcase, GraduationCap, HandHeart, BookOpen, Shield, FilePlus2, ArrowUpRight } from "lucide-react";
export default function HRDashboard() {
  const { user } = useAuth();
  const [overview, setOverview] = useState(null);
  const [directory, setDirectory] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/hr/dashboard");
        setOverview(data);
      } catch (e) {}
      try {
        const { data } = await api.get("/hr/directory");
        setDirectory(data.rows);
      } catch (e) {}
    })();
  }, []);

  const isInstitution = user?.role === "institution";
  const directoryLabel = isInstitution ? "Student directory" : "Employee directory";

  const t = overview?.totals || {};
  const tiles = [
    { icon: Briefcase, label: isInstitution ? "Students" : "Employees", v: t.employees ?? 0 },
    { icon: GraduationCap, label: "Interns", v: t.interns ?? 0 },
    { icon: HandHeart, label: "Volunteers", v: t.volunteers ?? 0 },
    { icon: BookOpen, label: "Training", v: t.training ?? 0 },
    { icon: Shield, label: "Conduct", v: t.conduct ?? 0 },
    { icon: Award, label: "Recommendations", v: t.recommendations_issued ?? 0 },
  ];

  return (
    <div className="min-h-screen bg-ec-bg" data-testid="hr-dashboard-page">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 sm:px-10 py-10">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <div className="eyebrow">{isInstitution ? "Institution" : "HR"} workspace</div>
            <h1 className="font-display text-4xl font-black tracking-tight mt-2">
              {isInstitution ? "Students & Alumni" : "Workforce credibility"}
            </h1>
            <p className="text-ec-ink-soft mt-2 max-w-xl">
              Track every certificate, recommendation and verified record you've issued. Every action is logged in your audit trail.
            </p>
          </div>
          <Link to="/certificates" data-testid="hr-issue-cert-link">
            <Button className="rounded-sm bg-ec-ink hover:bg-zinc-800 gap-2">
              <FilePlus2 className="w-4 h-4" /> Issue certificate
            </Button>
          </Link>
        </div>

        {/* Overview tiles */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-px bg-ec-line border border-ec-line mb-10" data-testid="hr-overview-tiles">
          {tiles.map((t) => (
            <div key={t.label} className="bg-white p-5">
              <t.icon className="w-5 h-5 text-ec-emerald" />
              <div className="font-display text-3xl font-black mt-3" data-testid={`hr-tile-${t.label.toLowerCase()}`}>{t.v}</div>
              <div className="eyebrow mt-1">{t.label}</div>
            </div>
          ))}
        </div>

        <Tabs defaultValue="directory" data-testid="hr-tabs">
          <TabsList className="bg-ec-mute rounded-sm">
            <TabsTrigger value="directory" className="rounded-sm gap-2" data-testid="hr-tab-directory">
              <Users className="w-4 h-4" /> {directoryLabel}
            </TabsTrigger>
            <TabsTrigger value="recent" className="rounded-sm gap-2" data-testid="hr-tab-recent">
              <Award className="w-4 h-4" /> Recent issued
            </TabsTrigger>
          </TabsList>

          <TabsContent value="directory" className="mt-6">
            <Card className="rounded-sm border-ec-line">
              <CardHeader className="border-b border-ec-line">
                <CardTitle className="font-display">{directoryLabel}</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {directory.length === 0 ? (
                  <div className="p-10 text-center text-sm text-ec-ink-soft">
                    No recipients yet. Issue your first certificate to populate this directory.
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-ec-mute text-left">
                        <tr>
                          <Th>Name</Th>
                          <Th>EthicCred ID</Th>
                          <Th>Score</Th>
                          <Th>Trust Tier</Th>
                          <Th>Certificates</Th>
                          <Th>Latest issue</Th>
                          <Th>Joined</Th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-ec-line">
                        {directory.map((d) => (
                          <tr key={d.ethic_id} className="hover:bg-ec-soft" data-testid={`directory-row-${d.ethic_id}`}>
                            <td className="px-4 py-3 font-display font-semibold">{d.name}</td>
                            <td className="px-4 py-3 font-mono-plex font-bold">{d.ethic_id}</td>
                            <td className="px-4 py-3 font-mono-plex">{d.score}</td>
                            <td className="px-4 py-3"><TierBadge tier={d.tier} size="sm" /></td>
                            <td className="px-4 py-3">{d.certificate_count}</td>
                            <td className="px-4 py-3 text-ec-ink-soft">{d.latest_issue}</td>
                            <td className="px-4 py-3 text-ec-ink-soft">{d.joining_date}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="recent" className="mt-6">
            <Card className="rounded-sm border-ec-line">
              <CardHeader className="border-b border-ec-line">
                <CardTitle className="font-display">Recent issued certificates</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {(overview?.recent_certificates || []).length === 0 ? (
                  <div className="p-10 text-center text-sm text-ec-ink-soft">No recent certificates.</div>
                ) : (
                  <ul className="divide-y divide-ec-line">
                    {(overview?.recent_certificates || []).map((c) => (
                      <li key={c.id} className="p-5 flex items-center justify-between">
                        <div>
                          <div className="font-display font-semibold">{c.title}</div>
                          <div className="text-sm text-ec-ink-soft">{c.cert_type} · {c.recipient_name} ({c.recipient_ethic_id}) · {c.issue_date}</div>
                        </div>
                        <Link to={`/verify/${c.id}`} target="_blank" className="text-ec-emerald font-semibold text-sm inline-flex items-center gap-1 hover:underline">
                          Verify <ArrowUpRight className="w-3.5 h-3.5" />
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        <ComplianceSection isInstitution={isInstitution} />
      </div>
      <Footer />
    </div>
  );
}

function ComplianceSection({ isInstitution }) {
  const orgDocs = [
    { slug: "employer-agreement", title: "Employer Authorization & Issuer Agreement" },
    { slug: "certificate-issuance-policy", title: "Certificate Issuance Policy" },
    { slug: "consent-verification-policy", title: "Consent & Verification Policy" },
    { slug: "trust-safety-policy", title: "Trust & Safety Enforcement Policy" },
    { slug: "dispute-resolution-policy", title: "Dispute Resolution Policy" },
  ];
  const instDocs = [
    { slug: "institution-agreement", title: "Institution Authorization & Issuer Agreement" },
    { slug: "certificate-issuance-policy", title: "Certificate Issuance Policy" },
    { slug: "consent-verification-policy", title: "Consent & Verification Policy" },
    { slug: "dispute-resolution-policy", title: "Dispute Resolution Policy" },
  ];
  const docs = isInstitution ? instDocs : orgDocs;
  return (
    <div className="mt-14 border-t border-ec-line pt-10" data-testid="compliance-section">
      <div className="eyebrow">Compliance</div>
      <h2 className="font-display text-2xl font-black tracking-tight mt-2">Your obligations as an issuer</h2>
      <p className="text-sm text-ec-ink-soft mt-2 max-w-2xl">
        These are the policies governing your ability to issue verified credentials on EthicCred.
        Click any document to review it in full.
      </p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-6">
        {docs.map((d) => (
          <Link
            key={d.slug}
            to={`/legal/${d.slug}`}
            className="border border-ec-line bg-white p-5 hover:bg-ec-soft transition-colors flex items-start gap-3"
            data-testid={`compliance-doc-${d.slug}`}
          >
            <Shield className="w-4 h-4 text-ec-emerald mt-1 flex-shrink-0" />
            <div>
              <div className="font-display font-semibold text-sm">{d.title}</div>
              <div className="text-xs text-ec-emerald font-semibold mt-1">Read policy →</div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

function Th({ children }) {
  return <th className="px-4 py-3 font-mono-plex text-[11px] font-semibold uppercase tracking-wider text-ec-ink-soft">{children}</th>;
}
